import JsonSchema from '../common/JsonSchema.model';

import { EntityAddress } from './EntityAddress.model';

export enum CustomPropertyType {
    integer = 'INTEGER',
    number = 'NUMBER',
    boolean = 'BOOLEAN',
    collaterals = 'COLLATERALS',
    equipment = 'EQUIPMENT',
    string = 'STRING',
    email = 'EMAIL',
    listString = 'LIST_STRING',
    listEmail = 'LIST_EMAIL',
    address = 'ADDRESS',
    credentialTypes = 'CREDENTIAL_TYPES',
    enum = 'ENUM',
}

export interface CustomProperty {
    id: string;
    key: string;
    name: string;
    required: boolean;
    description: string | null;
    schema: JsonSchema;
    createdAt: string;
    type: CustomPropertyType;
}

export interface EnumOptionUsage {
    entities: number;
    riskProfiles: number;
}

export interface CustomPropertiesPostResponse {
    successCount: number;
    failureCount: number;
    totalCount: number;
    successes: CustomProperty[];
    failures: { error: { message: string } }[];
}

export interface CustomPropertyAssignmentRuleUsage {
    ccgId: string;
    ccgDisplayName: string;
}

export interface CustomPropertyCriterionUsage {
    ccgId: string;
    ccgDisplayName: string;
    criterionId: string;
    coverageType: string;
    field: string;
    /** Human-readable requirement name, e.g. 'Commercial General Liability'. */
    requirementName: string;
    /** Human-readable criterion name, e.g. 'Each Occurrence Limit'. */
    criterionTitle: string;
}

/**
 * Everything referencing a custom property. Returned when opening the delete modal, and again in
 * the body of a 409 when a delete is rejected.
 */
export interface CustomPropertyUsage {
    assignmentRules: CustomPropertyAssignmentRuleUsage[];
    criteria: CustomPropertyCriterionUsage[];
    notificationTemplates: string[];
    isCollateralsProperty: boolean;
    entityValueCount: number;
}

/** Stored values do not block deletion — they are deleted along with the property. */
export function isCustomPropertyDeletable(usage: CustomPropertyUsage): boolean {
    return usage.assignmentRules.length === 0
        && usage.criteria.length === 0
        && usage.notificationTemplates.length === 0
        && !usage.isCollateralsProperty;
}

export interface CustomPropertyCollateral {
    description: string;
    category: string;
    limitRequired: number;
    uniqueIdentifier?: string;
    maximumDeductible?: number;
    id?: string;
}

export interface CustomPropertyEquipment {
    description: string;
    equipmentValue: number;
    rentalId?: string;
    rentalCompleteDate?: string;
    rentalProtectionPlan?: boolean;
}

export type CustomPropertyValue =
    string
    | number
    | boolean
    | object
    | undefined
    | string[]
    | CustomPropertyCollateral[]
    | CustomPropertyEquipment[]
    | EntityAddress;

export type CustomPropertyValues = Record<string, CustomPropertyValue>;
